import { useContext, useEffect, useState } from "react";
import { StyleContext } from "../../providers/StyleContextProvider";
import { useMediaQuery } from "react-responsive";
import { LoggedRecruiter } from "../../models/LoggedRecruiter";
import { AuthContext } from "../../providers/AuthContextProvider";
import { getCVLessAnswers, getCVBasedAnswers } from "../../services/RecruiterService";
import useErrorPage from "../../hooks/useErrorPage";
import { PreApplicationCandidateAnswer, PreApplicationQuestion } from "../../models/PreApplicationQuestionModel";
import { CandidateResult, JobQuestionCandidateAnswer, VacancyAnswersCandidateModel } from "../../models/CandidateVacancyAnswerModel";
import { ApplicationPopUpLoader } from "../Loaders/ApplicationPopUpLoader";
import { ReactComponent as Doubletick } from "../../assets/svg/double-tick.svg";
import { ReactComponent as Checkcircle } from "../../assets/svg/check-circle.svg";
import { ReactComponent as Closecircle } from "../../assets/svg/close-circle-red.svg";
import { JobApplication } from "../../models/JobApplication";

interface Props {
    selectedApplicant: JobApplication;
    jobId: number;
    isCvLess: boolean;
}

export const AnswersTab: React.FC<Props> = (props) => {
    const styleContext = useContext(StyleContext);
    const tabStyles = styleContext.getComponentStyle("applicationStatusTabs");  
    const styles = tabStyles.answerTab;  
    const isMobile = useMediaQuery({ query: "(max-width: 786px)"}); 
    const authContext = useContext(AuthContext);    
    const recruiter: LoggedRecruiter | null = authContext.getLoggedRecruiter();    
    const [cvLessQuestions, setCvLessQuestions] = useState<PreApplicationQuestion[]>([]);
    const [cvLessAnswers, setCvLessAnswers] = useState<PreApplicationCandidateAnswer[]>([]);
    const [questionsAndAnswers, setQuestionsAndAnswers] = useState<JobQuestionCandidateAnswer[]>([]);
    const [candidateResult, setCandidateResult] = useState<CandidateResult>();
    const [showFullAnswer, setShowFullAnswer] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [toErrorPage] = useErrorPage();

    useEffect(() => {
        if (props.isCvLess){
            getCVLessAnswerList(props.selectedApplicant.preApplicationId!);
        }
        else {
            getCVBasedAnswerList(props.selectedApplicant.jobApplicationId);
        }
    }, []);

    const getAnswerForQuestion = (questionId: number): string => {
        const answer = cvLessAnswers.find(a => a.preApplicationQuestionId === questionId);
        return answer ? answer.answer : "-";
    };

    const handleShowFullAnswer = () => {
        setShowFullAnswer(!showFullAnswer);
    };

    async function getCVLessAnswerList(preApplicationId: number) {
        setLoading(true);
        await getCVLessAnswers(preApplicationId, props.jobId, recruiter!.recruiterId, recruiter!.token)
        .then((response) => {
            setCvLessQuestions(response.preApplicationQuestions);
            setCvLessAnswers(response.preApplicationCandidateAnswers);
            setLoading(false);
        })
        .catch((error) => {            
            toErrorPage(error);
        });
    }

    async function getCVBasedAnswerList(jobApplicationId: number) {
        setLoading(true);
        await getCVBasedAnswers(jobApplicationId, recruiter!.recruiterId, props.selectedApplicant.candidate?.candidateId!, recruiter!.token)
        .then((response: VacancyAnswersCandidateModel) => {
            setQuestionsAndAnswers(response.questionsAndAnswers);
            setCandidateResult(response.candidateResult);
            setLoading(false);
        })
        .catch((error) => {            
            toErrorPage(error);
        });
    }

    return (
        <>
            <div style={styles.rowWrapper}>
                <div style={styles.title}>Answers</div>
                {!isMobile && candidateResult && <div style={styles.screeningStatus}>
                    {candidateResult.result ? <Doubletick style={styles.screeningStatus.icon}/> : <Closecircle style={styles.screeningStatus.icon}/>}
                    <div style={styles.screeningStatus.text}>{"Screening "}
                        {candidateResult.result ? <span style={styles.screeningStatus.bold}>{"Passed"}</span> : <span style={styles.screeningStatus.bold}>{"Failed"}</span>}
                    </div>
                </div>}
                {props.selectedApplicant.preApplicationId === 0 && candidateResult && (
                    <>
                        <div style={styles.total}>{"Total"}</div>
                        <div style={styles.score}>{candidateResult.score + "%"}</div>
                    </>
                )}
            </div>
            {isMobile && candidateResult && <div style={styles.screeningStatus}>
                {candidateResult.result ? <Doubletick style={styles.screeningStatus.icon}/> : <Closecircle style={styles.screeningStatus.icon}/>}
                <div style={styles.screeningStatus.text}>{"Screening "}
                    {candidateResult.result ? <span style={styles.screeningStatus.bold}>{"Passed"}</span> : <span style={styles.screeningStatus.bold}>{"Failed"}</span>}
                </div>
            </div>}
            {loading ? <ApplicationPopUpLoader/> :
                <div style={styles.questionWrapper}>
                    {(props.selectedApplicant.preApplicationId !== 0) ? (cvLessQuestions.length > 0 && 
                    cvLessQuestions.map((q, index) => (
                        <div key={q.preApplicationQuestionId} style={styles.question}>
                            <div style={styles.questionNumber}>{"Question " + (index + 1).toString().padStart(2, '0')}</div>
                            <div style={styles.question.row}>
                                <div style={styles.question.text}>{q.question}</div>
                            </div>
                            <div style={styles.answerWrapper}>
                                <div style={styles.answerWrapper.text}>{"Answer"}</div>
                                <div style={styles.answerWrapper.answers}>{getAnswerForQuestion(q.preApplicationQuestionId)}</div>
                            </div>
                        </div>
                    ))) : 
                    (questionsAndAnswers.length > 0 && 
                    questionsAndAnswers.map((qa, index) => (
                        <div key={qa.jobQuestion.jobQuestionId} style={styles.question}>
                            <div style={styles.questionNumber}>{"Question " + (index + 1).toString().padStart(2, '0')}</div>
                            <div style={styles.question.row}>
                                <div style={styles.question.text}>{qa.jobQuestion.question}{(qa.jobQuestion.questionTypeId === 6) && <span style={styles.question.knockout}>{"(knockout)"}</span>}</div>
                                {(qa.jobQuestion.questionTypeId !== 5 && qa.jobQuestion.questionTypeId !== 6) && <div style={styles.question.score}>{qa.score}</div>}
                                {(qa.jobQuestion.questionTypeId === 6) && (
                                    <div style={styles.question.score}>
                                        {qa.knockoutResult ? <Checkcircle style={styles.question.icon}/> : <Closecircle style={styles.question.icon}/>}
                                    </div>
                                )}
                            </div>
                            <div style={styles.answerWrapper}>
                                <div style={styles.answerWrapper.text}>{"Answer"}</div>
                                <div style={styles.answerWrapper.answers}>
                                    {qa.jobQuestion.questionTypeId === 2 ? (
                                        <ul style={styles.answerWrapper.list}>
                                            {qa.candidateAnswer.split(';').map((item, idx) => (
                                                <li key={idx}>{item.trim()}</li>
                                            ))}
                                        </ul>
                                    ) 
                                    : (
                                        <>
                                            {showFullAnswer
                                                ? qa.candidateAnswer
                                                : `${qa.candidateAnswer.substring(0, 180)}`
                                            }   
                                            {qa.candidateAnswer.length > 180 && (
                                                <span onClick={() => handleShowFullAnswer()} 
                                                    style={styles.answerWrapper.seeMore}
                                                >
                                                    {showFullAnswer ? "See Less" : "See More"}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        )
                    ))}
                </div>
            }
        </>
    );
}